/**
 * @description Super Admin create-CP form (LYN-156)
 *
 * Key features:
 * - Single-page CP form (not a TenantCreate wizard)
 * - v0 fields only: partner name, slug + host preview, Admin User + sendInvite
 * - Occupancy + ≤20 enforced server-side via validateSlug
 *
 * @dependencies
 * - adminApi: createChannelPartner()
 * - getChannelPartnerHostPreview
 *
 * @notes
 * - No template step (Accounting / Full Service / Custom). SO create is unchanged
 * - Blank canvas: no feature packs / cp_features / Configurations
 * - Submit spine is Wristband tenant → user → app:{env}:cp → Lynx rows
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';
import { createChannelPartner } from '../../services/api/adminApi';
import { getChannelPartnerHostPreview } from '../../config/domains';

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function autoSlug(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 20);
}

function ChannelPartnerCreate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [inviteNotice, setInviteNotice] = useState(null);
  const [createdHref, setCreatedHref] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    name: '',
    slug: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    sendInvite: false,
  });

  const updateForm = useCallback((field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'name' && prev.slug === autoSlug(prev.name)) {
        updated.slug = autoSlug(value);
      }
      return updated;
    });
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validate = () => {
    const errors = {};
    if (!form.name.trim() || form.name.trim().length < 2) {
      errors.name = 'Partner name is required (min 2 characters)';
    }
    if (!form.slug.trim()) {
      errors.slug = 'Slug is required';
    } else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(form.slug)) {
      errors.slug = 'Slug must be lowercase letters, numbers, and single hyphens';
    } else if (form.slug.length < 3 || form.slug.length > 20) {
      errors.slug = 'Slug must be 3-20 characters';
    }
    if (!form.adminFirstName.trim()) {
      errors.adminFirstName = 'First name is required';
    }
    if (!form.adminLastName.trim()) {
      errors.adminLastName = 'Last name is required';
    }
    if (!form.adminEmail.trim()) {
      errors.adminEmail = 'Admin email is required';
    } else if (!isValidEmail(form.adminEmail.trim())) {
      errors.adminEmail = 'Please enter a valid email address';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(null);
    setInviteNotice(null);
    const idempotencyKey = crypto.randomUUID();

    try {
      const result = await createChannelPartner({
        name: form.name.trim(),
        slug: form.slug.toLowerCase().trim(),
        adminEmail: form.adminEmail.trim().toLowerCase(),
        adminFirstName: form.adminFirstName.trim(),
        adminLastName: form.adminLastName.trim(),
        sendInvite: form.sendInvite === true,
      }, idempotencyKey);

      if (result.success) {
        if (form.sendInvite === true && result.data?.inviteSent !== true) {
          setInviteNotice(
            result.message
            || 'Channel partner created but invitation could not be sent. You can resend later.'
          );
          setCreatedHref(`/channel-partners/${result.data.id}`);
          return;
        }
        navigate(`/channel-partners/${result.data.id}`, { replace: true });
      }
    } catch (err) {
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;
      if (errorCode === 'INVALID_SLUG' || errorCode === 'SLUG_TOO_LONG') {
        setSubmitError(errorMessage || 'That slug is taken or reserved.');
      } else if (errorCode === 'CP_ROLE_MISSING' || errorCode === 'CP_ROLE_ASSIGN_FAILED') {
        setSubmitError(errorMessage || 'Wristband catalog role app:{env}:cp is missing. Create fails closed.');
      } else {
        setSubmitError(errorMessage || 'Failed to create channel partner.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hostPreview = getChannelPartnerHostPreview(form.slug || 'slug');

  return (
    <div className="max-w-xl">
      <button
        onClick={() => navigate('/channel-partners')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Channel Partners
      </button>
      <h1 className="text-xl font-semibold text-gray-900">Create Channel Partner</h1>
      <p className="text-sm text-gray-500 mt-0.5 mb-6">
        Stand up a CP tenant and bootstrap admin. No SO templates, no feature packs.
      </p>

      {submitError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>{submitError}</p>
        </div>
      )}
      {inviteNotice && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p>{inviteNotice}</p>
            {createdHref && (
              <button
                type="button"
                onClick={() => navigate(createdHref, { replace: true })}
                className="mt-2 text-sm font-medium text-amber-900 underline"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-gray-200 rounded-xl p-6">
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Partner name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateForm('name', e.target.value)}
              placeholder="Northwind Partners"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            {fieldErrors.name && <p className="text-xs text-red-600 mt-1">{fieldErrors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => updateForm('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 20))}
              maxLength={20}
              placeholder="northwind"
              className={`w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.slug ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            {fieldErrors.slug ? (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.slug}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                Host preview:{' '}
                <span className="font-medium text-gray-600">{hostPreview}</span>
              </p>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Admin User</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.adminFirstName}
                onChange={(e) => updateForm('adminFirstName', e.target.value)}
                placeholder="Pat"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                  fieldErrors.adminFirstName ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              />
              {fieldErrors.adminFirstName && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.adminFirstName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.adminLastName}
                onChange={(e) => updateForm('adminLastName', e.target.value)}
                placeholder="Lee"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                  fieldErrors.adminLastName ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              />
              {fieldErrors.adminLastName && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.adminLastName}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.adminEmail}
              onChange={(e) => updateForm('adminEmail', e.target.value)}
              placeholder="admin@northwind.example"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.adminEmail ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            {fieldErrors.adminEmail && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.adminEmail}</p>
            )}
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.sendInvite}
              onChange={(e) => updateForm('sendInvite', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              <span className="text-sm font-medium text-gray-700">Send invitation</span>
              <span className="block text-xs text-gray-500 mt-0.5">
                Email a branded invite so they can set a password. Leave unchecked to create only.
              </span>
            </span>
          </label>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Create channel partner
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChannelPartnerCreate;
